import React, { useCallback, useEffect, useRef, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import "./PWPagesV2.css";
import { useSiteContext } from "../SiteContext";
import { Page } from "../interfaces/PagesInterface";
import config from "../configLoader";
import {
    createPWClassification,
    deactivatePWClassification,
    activatePWClassification,
    getPWClassifications,
    patchPWClassification,
    getErrorMessage,
} from "../api/prevailingWage";
import { PWClassification } from "../interfaces/PrevailingWage";
import {
    Layers,
    ChevronRight,
    ChevronUp,
    Info,
    Check,
    Slash,
    Edit,
    Plus,
    X,
    AlertTriangle,
    CheckCircle,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type SortKey = "sort" | "alt" | "name" | "desc";
type FilterActive = "active" | "inactive";

interface ToastItem {
    id: number;
    kind: "ok" | "err";
    msg: string;
    undoFn?: () => void;
    out?: boolean;
}

const PAGE_SIZES = [15, 30, 50, 100];
const DASH = "—";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cmp(a: string | number | null | undefined, b: string | number | null | undefined): number {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function pageWindow(p: number, pages: number): (number | "…")[] {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const out: (number | "…")[] = [1];
    const lo = Math.max(2, p - 1);
    const hi = Math.min(pages - 1, p + 1);
    if (lo > 2) out.push("…");
    for (let i = lo; i <= hi; i++) out.push(i);
    if (hi < pages - 1) out.push("…");
    out.push(pages);
    return out;
}

// ─── Component ───────────────────────────────────────────────────────────────

const PWClassificationsPage: React.FC = () => {
    const { setLoading, createPopup, userRole, setPage } = useSiteContext();
    const isReadOnly = config.readOnlyPrevailingWagePages;

    // ── Data ──────────────────────────────────────────────────────────────
    const [items, setItems] = useState<PWClassification[] | null>(null);

    // ── Filter / sort / page state ────────────────────────────────────────
    const [filterActive, setFilterActive] = useState<FilterActive>("active");
    const [sortKey, setSortKey] = useState<SortKey>("sort");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
    const [page, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(30);

    // ── Modal state ───────────────────────────────────────────────────────
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<PWClassification | null>(null);
    const [mName, setMName] = useState("");
    const [mDesc, setMDesc] = useState("");
    const [mSort, setMSort] = useState("");
    const [mNameErr, setMNameErr] = useState(false);
    const [saving, setSaving] = useState(false);

    // ── Toasts ────────────────────────────────────────────────────────────
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const toastIdRef = useRef(0);

    // ── Access gate ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!userRole.admin) {
            createPopup("Access Denied", "You do not have permission to access this page.");
            setPage(Page.Dashboard);
            return;
        }
        if (!config.showPrevailingWagePages) {
            createPopup("Access Denied", "This feature is not available.");
            setPage(Page.Dashboard);
        }
    }, [userRole, setPage, createPopup]);

    // ── Load ──────────────────────────────────────────────────────────────
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getPWClassifications(false);
            setItems(data);
        } catch (e) {
            console.error(e);
            createPopup("Error", getErrorMessage(e));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createPopup/setLoading unstable from SiteContext; including them recreates load every provider render and the load effect refires forever (infinite loading)
    }, []);

    useEffect(() => {
        if (!userRole.admin || !config.showPrevailingWagePages) return;
        load();
    }, [userRole.admin, load]);

    // ── Toast ─────────────────────────────────────────────────────────────
    const showToast = useCallback((kind: "ok" | "err", msg: string, undoFn?: () => void) => {
        const id = ++toastIdRef.current;
        setToasts((prev) => [...prev, { id, kind, msg, undoFn }]);
        const duration = undoFn ? 5200 : 3400;
        const t1 = setTimeout(() => {
            setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, out: true } : t)));
            const t2 = setTimeout(
                () => setToasts((prev) => prev.filter((t) => t.id !== id)),
                220
            );
            return t2;
        }, duration);
        return t1;
    }, []);

    // ── Sort / filter / page ──────────────────────────────────────────────
    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
        setPageNum(1);
    };

    const handleFilter = (v: FilterActive) => {
        setFilterActive(v);
        setPageNum(1);
    };

    // ── Derived rows ──────────────────────────────────────────────────────
    const filteredRows = (items ?? []).filter((x) =>
        filterActive === "active" ? x.active : !x.active
    );

    const keyFns: Record<SortKey, (c: PWClassification) => string | number | null | undefined> = {
        sort: (c) => c.sortOrder,
        alt:  (c) => c.altPWClassificationID ?? null,
        name: (c) => c.name.toLowerCase(),
        desc: (c) => (c.description ?? "").toLowerCase(),
    };

    const sortedRows = filteredRows.slice().sort((a, b) => {
        const av = keyFns[sortKey](a);
        const bv = keyFns[sortKey](b);
        return (sortDir === "asc" ? 1 : -1) * cmp(av, bv);
    });

    const total = sortedRows.length;
    const pages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, pages);
    const view = sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);

    // ── Modal handlers ────────────────────────────────────────────────────
    const openCreate = () => {
        setEditing(null);
        setMName("");
        setMDesc("");
        setMSort("");
        setMNameErr(false);
        setModalOpen(true);
    };

    const openEdit = (row: PWClassification) => {
        setEditing(row);
        setMName(row.name);
        setMDesc(row.description ?? "");
        setMSort(row.sortOrder != null ? String(row.sortOrder) : "");
        setMNameErr(false);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditing(null);
    };

    const handleSave = async () => {
        const trimName = mName.trim();
        if (!trimName) {
            setMNameErr(true);
            return;
        }
        setMNameErr(false);
        const sortNum = mSort.trim() !== "" ? parseInt(mSort, 10) : undefined;
        setSaving(true);
        setLoading(true);
        try {
            if (editing) {
                const updated = await patchPWClassification(editing.pwClassificationID, {
                    name: trimName,
                    description: mDesc.trim() || null,
                    sortOrder: sortNum,
                });
                setItems((prev) =>
                    prev?.map((x) =>
                        x.pwClassificationID === editing.pwClassificationID ? { ...x, ...updated } : x
                    ) ?? prev
                );
                showToast("ok", `Classification <b>${trimName}</b> updated.`);
            } else {
                const created = await createPWClassification({
                    name: trimName,
                    description: mDesc.trim() || null,
                    sortOrder: sortNum,
                });
                setItems((prev) => (prev ? [...prev, created] : [created]));
                showToast("ok", `Classification <b>${trimName}</b> added.`);
            }
            closeModal();
        } catch (err) {
            showToast("err", getErrorMessage(err));
        } finally {
            setSaving(false);
            setLoading(false);
        }
    };

    // ── Toggle active / deactivate (with undo) ────────────────────────────
    const handleToggle = useCallback(
        async (row: PWClassification) => {
            const wasActive = row.active;
            // Optimistic update
            setItems((prev) =>
                prev?.map((x) =>
                    x.pwClassificationID === row.pwClassificationID
                        ? { ...x, active: !wasActive }
                        : x
                ) ?? prev
            );
            try {
                if (wasActive) {
                    await deactivatePWClassification(row.pwClassificationID);
                } else {
                    await activatePWClassification(row.pwClassificationID);
                }
                showToast(
                    "ok",
                    `<b>${row.name}</b> ${wasActive ? "deactivated" : "activated"}.`,
                    async () => {
                        // Undo: call the opposite endpoint
                        try {
                            if (wasActive) {
                                await activatePWClassification(row.pwClassificationID);
                            } else {
                                await deactivatePWClassification(row.pwClassificationID);
                            }
                            setItems((prev) =>
                                prev?.map((x) =>
                                    x.pwClassificationID === row.pwClassificationID
                                        ? { ...x, active: wasActive }
                                        : x
                                ) ?? prev
                            );
                        } catch (err) {
                            showToast("err", getErrorMessage(err));
                        }
                    }
                );
            } catch (err) {
                // Revert optimistic update
                setItems((prev) =>
                    prev?.map((x) =>
                        x.pwClassificationID === row.pwClassificationID
                            ? { ...x, active: wasActive }
                            : x
                    ) ?? prev
                );
                showToast("err", getErrorMessage(err));
            }
        },
        [showToast]
    );

    // ── Sortable th helper ────────────────────────────────────────────────
    const SortTh = ({
        label,
        sk,
        right,
    }: {
        label: string;
        sk: SortKey;
        right?: boolean;
    }) => {
        const on = sortKey === sk;
        return (
            <th
                className={`sortable${on ? " sorted" : ""}${right ? " right" : ""}`}
                onClick={() => handleSort(sk)}
            >
                <span className="th-in">
                    {label}
                    <ChevronUp
                        className="caret"
                        size={13}
                        style={{
                            transform: on && sortDir === "desc" ? "scaleY(-1)" : undefined,
                        }}
                    />
                </span>
            </th>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <PageWrapper title="PW Classifications" backgroundColor="#f4f5f7">
            <div className="pwv2">
                <div className="pw2-page">
                    {/* Breadcrumb */}
                    <div className="pw2-crumbs">
                        <span>Prevailing Wage</span>
                        <ChevronRight size={14} />
                        <span className="here">PW Classifications</span>
                    </div>

                    {/* Header */}
                    <div className="pw2-app-head">
                        <div className="pw2-logo">
                            <Layers size={23} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <h1 className="pw2-app-title">PW Classifications</h1>
                            <div className="pw2-app-sub">
                                The prevailing-wage classification catalog — Plumber, Laborer, and others.
                            </div>
                        </div>
                        {!isReadOnly && (
                            <div className="pw2-head-action">
                                <button className="pw2-btn primary" onClick={openCreate}>
                                    <Plus size={16} />
                                    Add classification
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Read-only note */}
                    {isReadOnly && (
                        <div className="pw2-ro-note">
                            <Info size={17} />
                            <span>
                                <b>Read-only on this environment.</b> Classifications are managed in the
                                primary payroll system.
                            </span>
                        </div>
                    )}

                    {/* Toolbar */}
                    <div className="pw2-toolbar">
                        <div className="pw2-segmented">
                            <button
                                className={filterActive === "active" ? "on" : ""}
                                onClick={() => handleFilter("active")}
                            >
                                Active
                            </button>
                            <button
                                className={filterActive === "inactive" ? "on" : ""}
                                onClick={() => handleFilter("inactive")}
                            >
                                Inactive
                            </button>
                        </div>
                    </div>

                    {/* Result line */}
                    <div className="pw2-result-line">
                        <span>
                            <b>{total}</b> {total === 1 ? "classification" : "classifications"}
                        </span>
                        <span className="rl-chip">
                            {filterActive === "active" ? "Active" : "Inactive"}
                        </span>
                    </div>

                    {/* Table */}
                    <div className="pw2-card">
                        <div className="pw2-tbl-wrap">
                            <table className="pw2-dt">
                                <thead>
                                    <tr>
                                        <SortTh label="ID" sk="alt" />
                                        <SortTh label="Name" sk="name" />
                                        <SortTh label="Description" sk="desc" />
                                        {!isReadOnly && (
                                            <th className="right">Actions</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {view.length === 0 ? (
                                        <tr>
                                            <td colSpan={isReadOnly ? 3 : 4} style={{ padding: 0 }}>
                                                <div className="pw2-empty">
                                                    <div className="eic">
                                                        <Layers />
                                                    </div>
                                                    <div className="et">No classifications found</div>
                                                    <p>
                                                        There are no{" "}
                                                        {filterActive === "inactive" ? "inactive" : "active"}{" "}
                                                        prevailing-wage classifications.
                                                    </p>
                                                    {filterActive === "inactive" && (
                                                        <button
                                                            className="pw2-btn sm"
                                                            style={{ marginTop: 6 }}
                                                            onClick={() => handleFilter("active")}
                                                        >
                                                            <X size={14} />
                                                            Show active
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        view.map((row) => (
                                            <tr
                                                key={row.pwClassificationID}
                                                className={
                                                    filterActive === "inactive" ? "inactive-row" : ""
                                                }
                                            >
                                                <td>
                                                    {row.altPWClassificationID ? (
                                                        <span className="mono strong">
                                                            {row.altPWClassificationID}
                                                        </span>
                                                    ) : (
                                                        <span className="dash">{DASH}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="pw2-name-cell">
                                                        <span
                                                            className={`nm${filterActive === "inactive" ? " dim" : ""}`}
                                                        >
                                                            {row.name}
                                                        </span>
                                                        {filterActive === "inactive" && (
                                                            <span className="pw2-pill inactive">
                                                                <span className="dot" />
                                                                Inactive
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    {row.description ? (
                                                        row.description
                                                    ) : (
                                                        <span className="dash">{DASH}</span>
                                                    )}
                                                </td>
                                                {!isReadOnly && (
                                                    <td className="right">
                                                        <div className="pw2-row-act">
                                                            <button
                                                                className="pw2-linkbtn"
                                                                onClick={() => openEdit(row)}
                                                            >
                                                                <Edit size={14} />
                                                                Edit
                                                            </button>
                                                            <button
                                                                className={`pw2-linkbtn${row.active ? " danger" : ""}`}
                                                                onClick={() => handleToggle(row)}
                                                            >
                                                                {row.active ? (
                                                                    <>
                                                                        <Slash size={14} />
                                                                        Deactivate
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Check size={14} />
                                                                        Activate
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination (conditional) */}
                        {total > pageSize && (
                            <div className="pw2-pager">
                                <div className="count">
                                    Showing{" "}
                                    <b>
                                        {(safePage - 1) * pageSize + 1}–
                                        {Math.min(safePage * pageSize, total)}
                                    </b>{" "}
                                    of <b>{total}</b>
                                </div>
                                <div className="psize">
                                    Rows{" "}
                                    <select
                                        className="pw2-select"
                                        value={pageSize}
                                        onChange={(e) => {
                                            setPageSize(Number(e.target.value));
                                            setPageNum(1);
                                        }}
                                    >
                                        {PAGE_SIZES.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {pages > 1 && (
                                    <div className="pages">
                                        <button
                                            className="pg nav"
                                            disabled={safePage === 1}
                                            onClick={() => setPageNum((p) => Math.max(1, p - 1))}
                                        >
                                            <ChevronRight
                                                size={16}
                                                style={{ transform: "scaleX(-1)" }}
                                            />
                                        </button>
                                        {pageWindow(safePage, pages).map((n, i) =>
                                            n === "…" ? (
                                                <span key={`el-${i}`} className="pg ellipsis">
                                                    …
                                                </span>
                                            ) : (
                                                <button
                                                    key={n}
                                                    className={`pg${n === safePage ? " on" : ""}`}
                                                    onClick={() => setPageNum(n as number)}
                                                >
                                                    {n}
                                                </button>
                                            )
                                        )}
                                        <button
                                            className="pg nav"
                                            disabled={safePage === pages}
                                            onClick={() => setPageNum((p) => Math.min(pages, p + 1))}
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Add/Edit modal */}
                    {modalOpen && (
                        <div className="pw2-modal-bg" onClick={closeModal}>
                            <div
                                className="pw2-modal"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="pw2-modal-head">
                                    <span className="mi">
                                        <Layers size={21} />
                                    </span>
                                    <div>
                                        <h3>
                                            {editing
                                                ? "Edit classification"
                                                : "Add classification"}
                                        </h3>
                                        <div className="ms">
                                            {editing
                                                ? editing.name
                                                : "Add a prevailing-wage classification to the catalog."}
                                        </div>
                                    </div>
                                    <button className="x" onClick={closeModal}>
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="pw2-modal-body">
                                    <div className="pw2-frow">
                                        <label>
                                            Name <span className="req">*</span>
                                        </label>
                                        <input
                                            className={`pw2-input${mNameErr ? " err" : ""}`}
                                            value={mName}
                                            placeholder="e.g. Plumber"
                                            onChange={(e) => {
                                                setMName(e.target.value);
                                                if (mNameErr && e.target.value.trim())
                                                    setMNameErr(false);
                                            }}
                                            autoFocus
                                        />
                                        {mNameErr && (
                                            <div className="pw2-inline-err">
                                                <AlertTriangle size={15} />
                                                Name is required.
                                            </div>
                                        )}
                                    </div>
                                    <div className="pw2-frow">
                                        <label>
                                            Description{" "}
                                            <span className="opt">optional</span>
                                        </label>
                                        <textarea
                                            className="pw2-textarea"
                                            value={mDesc}
                                            placeholder="Short description of this classification."
                                            onChange={(e) => setMDesc(e.target.value)}
                                        />
                                    </div>
                                    <div className="pw2-frow">
                                        <label>
                                            Sort order{" "}
                                            <span className="opt">optional</span>
                                        </label>
                                        <input
                                            className="pw2-input"
                                            type="number"
                                            style={{ maxWidth: 160 }}
                                            value={mSort}
                                            placeholder="10"
                                            onChange={(e) => setMSort(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="pw2-modal-foot">
                                    <button
                                        className="pw2-btn ghost"
                                        onClick={closeModal}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="pw2-btn primary"
                                        onClick={handleSave}
                                        disabled={saving}
                                    >
                                        <Check size={16} />
                                        {editing ? "Save changes" : "Add classification"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Toasts */}
                    {toasts.length > 0 && (
                        <div className="pw2-toast-wrap">
                            {toasts.map((t) => (
                                <div
                                    key={t.id}
                                    className={`pw2-toast ${t.kind}${t.out ? " out" : ""}`}
                                >
                                    {t.kind === "ok" ? (
                                        <CheckCircle size={18} />
                                    ) : (
                                        <AlertTriangle size={18} />
                                    )}
                                    <span
                                        className="t-msg"
                                        dangerouslySetInnerHTML={{ __html: t.msg }}
                                    />
                                    {t.undoFn && (
                                        <button
                                            className="t-undo"
                                            onClick={() => {
                                                setToasts((prev) =>
                                                    prev.filter((x) => x.id !== t.id)
                                                );
                                                t.undoFn!();
                                            }}
                                        >
                                            Undo
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </PageWrapper>
    );
};

export default PWClassificationsPage;
